import React from 'react';
import {
  Grid,
  TextField,
  IconButton,
  Typography,
  Box,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { FundingAllocation, RestrictionType } from '../../types/accounting';

interface FundingAllocationInputsProps {
  allocations: FundingAllocation[];
  onAllocationsChange: (allocations: FundingAllocation[]) => void;
  variant?: 'journal' | 'lineItem';
}

export const FundingAllocationInputs: React.FC<FundingAllocationInputsProps> = ({
  allocations,
  onAllocationsChange,
  variant = 'journal'
}) => {
  const addAllocation = () => {
    const newAllocation: FundingAllocation = {
      fundId: '',
      sourceId: '',
      sourceType: 'general',
      amount: 0,
      percentage: 0,
      restrictionType: RestrictionType.UNRESTRICTED
    };
    onAllocationsChange([...allocations, newAllocation]);
  };

  const removeAllocation = (index: number) => {
    const newAllocations = [...allocations];
    newAllocations.splice(index, 1);
    onAllocationsChange(newAllocations);
  };

  const updateAllocation = (index: number, field: keyof FundingAllocation, value: any) => {
    const newAllocations = [...allocations];
    newAllocations[index] = {
      ...newAllocations[index],
      [field]: value
    };
    onAllocationsChange(newAllocations);
  };

  const handlePercentageChange = (index: number, value: number) => {
    updateAllocation(index, 'percentage', value);
  };

  return (
    <Box>
      {allocations.map((allocation, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Funding Type"
              value={allocation.sourceType}
              onChange={(e) => updateAllocation(index, 'sourceType', e.target.value)}
              sx={{
                '& .MuiSelect-select': {
                  color: '#fff',
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                  },
                },
              }}
            >
              <MenuItem value="grant">Grant</MenuItem>
              <MenuItem value="program">Program</MenuItem>
              <MenuItem value="campaign">Campaign</MenuItem>
              <MenuItem value="general">General Fund</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Amount"
              value={allocation.amount}
              onChange={(e) => updateAllocation(index, 'amount', parseFloat(e.target.value))}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Percentage"
              value={allocation.percentage || ''}
              onChange={(e) => handlePercentageChange(index, parseFloat(e.target.value))}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography sx={{ color: 'primary.light' }}>%</Typography>
                  </InputAdornment>
                ),
                sx: { color: 'text.primary' }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <IconButton 
              onClick={() => removeAllocation(index)}
              sx={{ mt: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      
      <Box sx={{ mt: 2 }}>
        <IconButton 
          onClick={addAllocation}
          sx={{ 
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'rgba(144, 202, 249, 0.08)',
            },
          }}
        >
          <AddIcon />
        </IconButton>
        <Typography variant="caption" sx={{ ml: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
          Add Funding Source
        </Typography>
      </Box>
    </Box>
  );
}; 